// Final landing-page reset -- the free Snapshot page. Second step of
// Homepage -> Snapshot -> Signup. Receives the domain submitted on the
// homepage hero via ?domain=, calls the one new backend endpoint
// (POST /api/public/snapshot -- see server.py's public_brand_snapshot,
// a single rate-limited, cached, real Tavily check, never fabricated
// data), and shows exactly what was genuinely computed. Everything
// this page cannot honestly compute pre-signup (competitor signals,
// discovery across search/social, a recommended action) is shown
// visibly locked, not faked -- per the AF-3.22 audit, none of that
// requires new invention here: it's precisely what the real,
// authenticated VisibilityService/OpportunityEngine pipeline already
// computes once a workspace exists.
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, CheckCircle, MagnifyingGlass } from '@phosphor-icons/react';
import { GLIMState } from '@/brand';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function looksLikeDomain(value) {
  const t = (value || '').trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(t) ? t.toLowerCase() : null;
}

const LOCKED_ROWS = [
  { label: 'Search & social discovery', desc: 'Where else your brand already surfaces beyond AI answers' },
  { label: 'Competitor signals', desc: 'Who else is being named alongside you, and how often' },
  { label: 'Recommended next action', desc: 'One specific step for your team, ranked against your other opportunities' },
];

export default function SnapshotPage() {
  const navigate = useNavigate();
  const [domain, setDomain] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | unavailable
  const [result, setResult] = useState(null);

  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('domain');
    const clean = looksLikeDomain(raw || '');
    if (!clean) {
      navigate('/', { replace: true });
      return;
    }
    setDomain(clean);

    let cancelled = false;
    axios.post(`${API}/public/snapshot`, { domain: clean })
      .then((res) => {
        if (cancelled) return;
        setResult(res.data);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('unavailable');
      });
    return () => { cancelled = true; };
  }, [navigate]);

  const handleContinue = () => {
    navigate(`/login?url=${encodeURIComponent(domain || '')}`);
  };

  return (
    <div className="min-h-screen bg-[var(--warm-canvas)]" data-testid="snapshot-page">
      <header className="border-b border-[var(--warm-border)] px-6 sm:px-10 py-5">
        <Link to="/" className="font-display text-[16px] font-extrabold text-[var(--text-primary)]" data-testid="snapshot-logo">
          MARTIAN
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <div className="inline-flex items-center gap-2 rounded-full border border-[var(--warm-border-strong)] bg-[var(--warm-canvas-alt)] px-3.5 py-1 text-[12px] font-semibold text-[var(--text-secondary)] mb-6" data-testid="snapshot-domain-badge">
          <MagnifyingGlass size={14} />
          {domain || '…'}
        </div>

        <h1 className="font-display font-extrabold text-[var(--text-primary)] text-balance" style={{ fontSize: 'clamp(1.7rem, 1.4rem + 1.4vw, 2.4rem)', lineHeight: 1.18 }} data-testid="snapshot-heading">
          Here's what Martian found about {domain || 'your brand'}.
        </h1>

        <div className="mt-9 bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-[var(--shadow-card)] overflow-hidden" data-testid="snapshot-card">
          {status === 'loading' && (
            <div className="px-6 py-10 flex flex-col items-center gap-3 text-[14px] text-[var(--text-tertiary)]" data-testid="snapshot-loading">
              <GLIMState state="analyzing" size={88} showLabel={false} />
              Checking {domain}…
            </div>
          )}

          {status === 'ready' && result && (
            <div className="divide-y divide-[var(--border)]" data-testid="snapshot-real-result">
              <div className="flex items-start gap-3 px-6 py-5">
                {result.mentioned_by_ai ? (
                  <GLIMState state="recommending" size={44} showLabel={false} className="flex-shrink-0" />
                ) : (
                  <CheckCircle size={20} weight="fill" className="text-[var(--text-tertiary)] mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-1">AI visibility</p>
                  <p className="text-[14.5px] text-[var(--text-primary)]" data-testid="snapshot-mention-status">
                    {result.mentioned_by_ai
                      ? `AI search already surfaces ${domain} when asked what it is.`
                      : `AI search doesn't yet clearly surface ${domain} when asked what it is — an early opportunity.`}
                  </p>
                </div>
              </div>
              {result.answer_snippet && (
                <div className="px-6 py-5" data-testid="snapshot-answer-snippet">
                  <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-1">What AI search said</p>
                  <p className="text-[13.5px] text-[var(--text-secondary)] leading-relaxed">"{result.answer_snippet}"</p>
                </div>
              )}
            </div>
          )}

          {status === 'unavailable' && (
            <div className="px-6 py-8 text-center" data-testid="snapshot-unavailable">
              <p className="text-[14px] text-[var(--text-secondary)]">
                We couldn't complete a live check for {domain} right now — your full snapshot is ready the moment your workspace is connected.
              </p>
            </div>
          )}

          {LOCKED_ROWS.map((row) => (
            <div key={row.label} className="flex items-start gap-3 px-6 py-5 border-t border-[var(--border)] bg-[var(--warm-canvas-alt)]/40" data-testid="snapshot-locked-row">
              <Lock size={18} className="text-[var(--text-tertiary)] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[14px] font-medium text-[var(--text-primary)]">{row.label}</p>
                <p className="text-[12.5px] text-[var(--text-tertiary)] mt-0.5">{row.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleContinue}
          data-testid="snapshot-continue-cta"
          className="mt-8 w-full sm:w-auto bg-[var(--text-primary)] hover:bg-[var(--text-secondary)] text-white font-semibold text-[15px] px-7 py-3.5 rounded-[var(--radius-button)] transition-colors"
        >
          Create your free workspace to unlock the full picture →
        </button>
        <p className="mt-3 text-[12.5px] text-[var(--text-tertiary)]">No need to re-enter {domain} — it carries straight into your workspace.</p>
      </main>
    </div>
  );
}
